<template>
  <!-- A ticket's conversation: the request at the top, the messages under it
       — this side's on the right, the other side's on the left — with a line
       for each move along the ticket's lifecycle (picked up, in progress,
       resolved, closed, re-opened), and a composer that sends text and files.
       It keeps itself current while it is open (a poll every
       TICKET_THREAD_POLL_MS), and tells the API this side has read what
       arrived, which is what clears the unread badges and stops the "you have
       an unread message" email. The same component lives in the admin and in
       both organization apps, which name their support team (`teamName`). -->
  <div
    class="tc"
    :class="{ 'tc--dragging': isDragging }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div class="tc__body">
      <div ref="thread" class="tc__thread" @scroll.passive="onThreadScroll">
        <!-- The request that opened the ticket: the first message. -->
        <article class="tc-request">
          <header class="tc-request__head">
            <span class="row-avatar tc-avatar" :class="requesterAvatarClass">{{ requesterInitials }}</span>
            <div class="tc-request__who">
              <span class="tc-request__name">{{ requesterName }}</span>
              <span class="tc-request__when">
                Opened {{ messageTimeLabel(ticket.createdAt) }}<template v-if="organizationName && isStaffViewer"> · {{ organizationName }}</template>
              </span>
            </div>
          </header>
          <h6 class="tc-request__subject">{{ ticket.subject }}</h6>
          <p class="tc-request__details">{{ ticket.details }}</p>
          <ticket-attachment-list v-if="requestAttachments.length" :attachments="requestAttachments" />
        </article>

        <div v-if="isLoading && !messages.length" class="tc-loading" role="status">
          <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          <span>Loading conversation…</span>
        </div>
        <p v-else-if="!messages.length" class="tc-empty">
          {{ canReply ? 'No replies yet. Write the first one below.' : 'No replies on this ticket.' }}
        </p>

        <template v-for="entry in timeline" :key="entry.key">
          <div v-if="entry.kind === 'day'" class="tc-day" role="separator"><span>{{ entry.label }}</span></div>
          <div v-else-if="entry.kind === 'unread'" class="tc-unread" role="separator"><span>New messages</span></div>
          <!-- A move along the lifecycle: who did what, when — and the remark
               that went with it. -->
          <div v-else-if="entry.kind === 'event'" class="tc-event" :class="`tc-event--${entry.tone}`">
            <p class="tc-event__line">
              <span class="tc-event__icon" aria-hidden="true"><i class="pi" :class="entry.icon"></i></span>
              <span class="tc-event__text"><strong>{{ entry.author }}</strong> {{ entry.did }}</span>
              <span class="tc-event__time" :title="entry.fullTime">{{ entry.time }}</span>
            </p>
            <p v-if="entry.text" class="tc-event__note">{{ entry.text }}</p>
            <ticket-attachment-list v-if="entry.attachments.length" :attachments="entry.attachments" />
          </div>
          <div v-else class="tc-msg" :class="entry.mine ? 'tc-msg--mine' : 'tc-msg--theirs'">
            <div class="tc-msg__meta">
              <span class="tc-msg__author">{{ entry.author }}</span>
              <span class="tc-msg__time" :title="entry.fullTime">{{ entry.time }}</span>
            </div>
            <div v-if="entry.text" class="tc-msg__bubble">{{ entry.text }}</div>
            <ticket-attachment-list
              v-if="entry.attachments.length"
              :attachments="entry.attachments"
              :align="entry.mine ? 'end' : 'start'"
            />
            <span v-if="entry.seen" class="tc-msg__seen">
              <i class="pi pi-check-circle" aria-hidden="true"></i> Seen
            </span>
          </div>
        </template>
      </div>

      <!-- Scrolled up when something new arrived: a way down to it. -->
      <button v-if="showJumpToLatest" type="button" class="tc-jump" @click="scrollToBottom(true)">
        <i class="pi pi-arrow-down" aria-hidden="true"></i>
        <span>New messages</span>
      </button>
    </div>

    <form class="tc-composer" @submit.prevent="send">
      <div v-if="!canReply" class="tc-closed">
        <i class="pi pi-lock" aria-hidden="true"></i>
        <span>This ticket is closed. Re-open it to continue the conversation.</span>
      </div>
      <template v-else>
        <ticket-attachment-picker ref="picker" v-model="files" compact :disabled="sending" />
        <div class="tc-composer__row">
          <textarea
            ref="input"
            v-model="draft"
            class="tc-composer__input"
            rows="1"
            :placeholder="placeholder"
            :disabled="sending"
            aria-label="Your reply"
            @keydown.enter.exact="onEnter"
            @paste="onPaste"
            @input="autogrow"
          ></textarea>
          <button
            type="button"
            class="tc-composer__attach"
            :disabled="sending"
            title="Attach files"
            aria-label="Attach files"
            @click="pickFiles"
          >
            <i class="pi pi-paperclip"></i>
          </button>
          <button
            type="submit"
            class="btn btn-custom-primary tc-composer__send"
            :disabled="!canSend"
            :title="sending ? 'Sending…' : 'Send'"
          >
            <span v-if="sending" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <template v-else>
              <i class="pi pi-send" aria-hidden="true"></i>
              <span>Send</span>
            </template>
          </button>
        </div>
        <p class="tc-composer__hint">{{ composerHint }}</p>
      </template>
    </form>
  </div>
</template>
<script>
import ticketAttachmentPicker from '@/components/ticket/Ticket_Attachment_Picker.vue';
import ticketAttachmentList from '@/components/ticket/Ticket_Attachment_List.vue';
import { ADD_TICKET_COMMENT, CLEAR_TICKET_COMMENTS, GET_TICKET_COMMENTS, MARK_TICKET_READ } from '@/store/action_types';
import { TICKET_COMMENT_TYPES, TICKET_THREAD_POLL_MS } from '@/utils/const';
import { ticketEventOf } from '@/utils/ticket_lifecycle';
import {
  avatarColorClass,
  dateOf,
  initialsOf,
  isSameDay,
  messageDayLabel,
  messageTimeLabel,
  personName,
  ticketSideOf,
  TICKET_SIDE_STAFF,
  viewerTicketSide,
} from '@/utils/ticket';

const TYPE_COMMENT = TICKET_COMMENT_TYPES[2];

// How close to the end the thread must be to follow new messages down.
const STICK_THRESHOLD_PX = 80;
const INPUT_MAX_HEIGHT_PX = 160;

const hasFiles = (event) => Array.from(event?.dataTransfer?.types || []).includes('Files');

// Whether the person has put the focus somewhere — anything but the page or
// the dialog the conversation is in.
const focusIsTaken = () => {
  const active = document.activeElement;
  return Boolean(active && active !== document.body && active.getAttribute('role') !== 'dialog');
};

// Text copied from Excel or Word, which comes with a picture of itself. Office
// always puts RTF on the clipboard, and nothing else that brings a file does;
// without it, rich text counts when its plain text is neither the address nor
// the name of the pasted file (as a copied image or a Finder copy carry).
const isOfficeText = (clipboard) => {
  const text = clipboard.getData('text/plain').trim();
  const types = Array.from(clipboard.types || []);
  if (!text) return false;
  if (types.includes('text/rtf')) return true;
  if (!types.includes('text/html') || /^(\S+:\/\/|data:)\S+$/.test(text)) return false;
  return !Array.from(clipboard.files).some((file) => file.name === text);
};

export default {
  name: 'TicketConversation',
  components: {
    ticketAttachmentPicker,
    ticketAttachmentList,
  },
  props: {
    ticket: {
      type: Object,
      required: true,
    },
    // False once the ticket is closed: the composer gives way to a note.
    canReply: {
      type: Boolean,
      default: true,
    },
    // Who the organization's side writes to: the support team, by the name
    // its app knows it by.
    teamName: {
      type: String,
      default: 'Screenkode',
    },
  },
  emits: ['ticket-updated', 'sent', 'read'],
  data() {
    const account = this.$store.getters.account.data;
    const mySide = this.ticket.viewerSide || viewerTicketSide(account);
    return {
      draft: '',
      files: [],
      sending: false,
      // Where "new" began when the conversation was opened: the other side's
      // messages after this side's last read mark. Kept as it was, so the
      // divider stays put while reading, until the conversation is closed.
      newSince: dateOf(mySide === TICKET_SIDE_STAFF ? this.ticket.staffLastReadAt : this.ticket.organizationLastReadAt),
      hadUnread: Number(this.ticket.unreadCount) > 0,
      stickToBottom: true,
      showJumpToLatest: false,
      dragDepth: 0,
      // On a touch keyboard Enter makes a new line; Send is the button.
      isTouch: typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    // Which side of the conversation this app is on: as the API says — or,
    // where it has not, the app's (in an organization's app, the organization).
    mySide() {
      return this.ticket.viewerSide || viewerTicketSide(this.account);
    },
    thread() {
      return this.$store.getters.ticketComments;
    },
    // The ticket as the API last described it with the conversation (status,
    // read marks), over what the list knew.
    liveTicket() {
      const current = this.thread?.ticket;
      return current && current.id === this.ticket.id ? { ...this.ticket, ...current } : this.ticket;
    },
    messages() {
      const current = this.thread?.ticket;
      if (current && current.id !== this.ticket.id) return [];
      return Array.isArray(this.thread?.data) ? this.thread.data : [];
    },
    isLoading() {
      return Boolean(this.thread?.isLoading);
    },
    isDragging() {
      return this.dragDepth > 0;
    },
    // Staff see which organization a ticket is from; the organization's own
    // people need not be told.
    isStaffViewer() {
      return this.mySide === TICKET_SIDE_STAFF;
    },
    requestAttachments() {
      return Array.isArray(this.ticket.attachments) ? this.ticket.attachments : [];
    },
    requesterName() {
      return personName(this.ticket.user, 'Requester');
    },
    requesterInitials() {
      return initialsOf(this.requesterName);
    },
    requesterAvatarClass() {
      return avatarColorClass(this.requesterName);
    },
    organizationName() {
      return this.ticket.organization?.name || '';
    },
    // When the other side last opened the conversation: what "Seen" is read from.
    otherSideReadAt() {
      return dateOf(this.mySide === TICKET_SIDE_STAFF
        ? this.liveTicket.organizationLastReadAt
        : this.liveTicket.staffLastReadAt);
    },
    // The messages with a separator at each new day, and one before the first
    // message that was unread when the conversation was opened. A move along
    // the lifecycle is a line of its own; "Seen" goes under this side's last
    // message.
    timeline() {
      const entries = [];
      let previousDay = null;
      let unreadShown = false;
      let lastMineIndex = -1;
      this.messages.forEach((message, index) => {
        if (ticketSideOf(message) === this.mySide && !ticketEventOf(message.type)) lastMineIndex = index;
      });

      this.messages.forEach((message, index) => {
        const at = dateOf(message.createdAt);
        if (at && (!previousDay || !isSameDay(previousDay, at))) {
          entries.push({ kind: 'day', key: `day-${at.toDateString()}`, label: messageDayLabel(at) });
          previousDay = at;
        }

        const mine = ticketSideOf(message) === this.mySide;
        if (!mine && !unreadShown && this.hadUnread && at && (!this.newSince || at > this.newSince)) {
          entries.push({ kind: 'unread', key: 'unread' });
          unreadShown = true;
        }

        const event = ticketEventOf(message.type);
        entries.push({
          kind: event ? 'event' : 'message',
          key: message.id || `message-${index}`,
          mine,
          author: this.authorLabel(message),
          time: messageTimeLabel(at),
          fullTime: at ? at.toLocaleString() : '',
          text: String(message.comment || '').trim(),
          attachments: Array.isArray(message.attachments) ? message.attachments : [],
          // "<who> started working on your ticket", as this side reads it.
          did: event ? (this.isStaffViewer ? event.staff : event.organization) : '',
          icon: event ? event.icon : '',
          tone: event ? event.tone : '',
          seen: mine && index === lastMineIndex && Boolean(this.otherSideReadAt && at && this.otherSideReadAt >= at),
        });
      });

      return entries;
    },
    canSend() {
      return this.canReply && !this.sending && (this.draft.trim().length > 0 || this.files.length > 0);
    },
    placeholder() {
      return this.mySide === TICKET_SIDE_STAFF ? `Reply to ${this.requesterName}…` : `Write a reply to ${this.teamName}…`;
    },
    composerHint() {
      return this.isTouch
        ? 'Attach screenshots or files with the paperclip.'
        : 'Enter to send · Shift+Enter for a new line · drop or paste files to attach';
    },
  },
  methods: {
    messageTimeLabel,
    // Who wrote a message, as this side knows them.
    authorLabel(message) {
      const isMe = Boolean(message.user?.id && this.account?.id && message.user.id === this.account.id);
      if (isMe) return 'You';
      if (ticketSideOf(message) === TICKET_SIDE_STAFF) {
        return this.mySide === TICKET_SIDE_STAFF ? personName(message.user, this.teamName) : this.teamName;
      }
      return personName(message.user, 'Requester');
    },
    isVisible() {
      return typeof document === 'undefined' || document.visibilityState !== 'hidden';
    },
    newestFromOtherSide() {
      for (let index = this.messages.length - 1; index >= 0; index -= 1) {
        if (ticketSideOf(this.messages[index]) !== this.mySide) return this.messages[index];
      }
      return null;
    },

    /* ------------------------------ loading ------------------------------ */
    async load({ silent = false } = {}) {
      const ok = await this.$store.dispatch(GET_TICKET_COMMENTS, { ticketId: this.ticket.id, silent });
      if (!ok || this.isGone) return false;
      this.$emit('ticket-updated', this.liveTicket);
      return true;
    },
    // After a load: the thread follows what arrived when it was at the end,
    // offers a way down to it when it was not, and says it has been read.
    async settle({ initial = false } = {}) {
      await this.$nextTick();
      if (this.isGone) return;
      const count = this.messages.length;
      const grew = count > this.seenCount;
      this.seenCount = count;
      if (initial) this.scrollToBottom(false);
      else if (grew && this.stickToBottom) this.scrollToBottom(true);
      else if (grew) this.showJumpToLatest = true;
      this.maybeMarkRead(initial);
    },
    // Tells the API this side has read the conversation — on opening it, and
    // whenever a new message from the other side has arrived while it is open.
    maybeMarkRead(force = false) {
      if (!this.isVisible()) return;
      const newest = this.newestFromOtherSide();
      const marker = newest ? newest.id : 'request';
      if (!force && marker === this.markedFor) return;
      this.markedFor = marker;
      this.$store.dispatch(MARK_TICKET_READ, { id: this.ticket.id }).then((ok) => {
        if (ok && !this.isGone) this.$emit('read');
      });
    },
    async refresh() {
      if (this.refreshing) return;
      this.refreshing = true;
      try {
        if (await this.load({ silent: true })) await this.settle();
      } finally {
        this.refreshing = false;
      }
    },
    /** For the parent, after it changed the ticket (closed or re-opened it). */
    reload() {
      return this.refresh();
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = window.setInterval(() => {
        if (this.isVisible() && !this.sending) this.refresh();
      }, TICKET_THREAD_POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    onVisibilityChange() {
      if (this.isVisible()) this.refresh();
    },

    /* ----------------------------- scrolling ----------------------------- */
    scrollToBottom(smooth) {
      const thread = this.$refs.thread;
      if (!thread) return;
      if (typeof thread.scrollTo === 'function') {
        thread.scrollTo({ top: thread.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      } else {
        thread.scrollTop = thread.scrollHeight;
      }
      this.stickToBottom = true;
      this.showJumpToLatest = false;
    },
    onThreadScroll() {
      const thread = this.$refs.thread;
      if (!thread) return;
      const gap = thread.scrollHeight - thread.scrollTop - thread.clientHeight;
      this.stickToBottom = gap < STICK_THRESHOLD_PX;
      if (this.stickToBottom) this.showJumpToLatest = false;
    },

    /* ----------------------------- composing ----------------------------- */
    onEnter(event) {
      if (this.isTouch) return;
      event.preventDefault();
      this.send();
    },
    // Pasted files and screenshots are attached; text from Office is pasted
    // as the text it is.
    onPaste(event) {
      const data = event.clipboardData;
      if (!data?.files?.length || isOfficeText(data)) return;
      event.preventDefault();
      this.addFiles(data.files);
    },
    pickFiles() {
      this.$refs.picker?.open();
    },
    addFiles(list) {
      if (!this.canReply || !list || !list.length) return;
      this.$refs.picker?.addFiles(list);
    },
    onDragEnter(event) {
      if (!this.canReply || !hasFiles(event)) return;
      this.dragDepth += 1;
    },
    onDragOver(event) {
      if (event.dataTransfer) event.dataTransfer.dropEffect = this.canReply && hasFiles(event) ? 'copy' : 'none';
    },
    onDragLeave() {
      if (this.dragDepth > 0) this.dragDepth -= 1;
    },
    onDrop(event) {
      this.dragDepth = 0;
      this.addFiles(event.dataTransfer?.files);
    },
    autogrow() {
      const input = this.$refs.input;
      if (!input) return;
      input.style.height = 'auto';
      input.style.height = `${Math.min(input.scrollHeight, INPUT_MAX_HEIGHT_PX)}px`;
    },
    async send() {
      if (!this.canSend) return;
      this.sending = true;
      let ok = false;
      // However the send ends, the composer is given back with what was in it.
      try {
        ok = await this.$store.dispatch(ADD_TICKET_COMMENT, {
          ticketId: this.ticket.id,
          comment: this.draft.trim(),
          type: TYPE_COMMENT,
          attachments: this.files,
        });
      } finally {
        this.sending = false;
      }
      if (!ok || this.isGone) return;

      this.draft = '';
      this.files = [];
      this.$nextTick(() => {
        this.autogrow();
        if (!this.isTouch) this.$refs.input?.focus();
      });
      this.stickToBottom = true;
      await this.refresh();
      this.$emit('sent');
    },
  },
  created() {
    // Plain fields: nothing in the template depends on them.
    this.seenCount = 0;
    this.markedFor = null;
    this.isGone = false;
    this.refreshing = false;
    this.pollTimer = null;
  },
  async mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    await this.$store.dispatch(CLEAR_TICKET_COMMENTS);
    if (await this.load()) await this.settle({ initial: true });
    if (this.isGone) return;
    this.startPolling();
    // The reply box takes the focus once the conversation is in, unless the
    // person went somewhere meanwhile (the ticket's title, a remark).
    if (!this.isTouch) {
      this.$nextTick(() => {
        if (!focusIsTaken()) this.$refs.input?.focus();
      });
    }
  },
  beforeUnmount() {
    this.isGone = true;
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
};
</script>
